import { createAction, createReducer } from '@reduxjs/toolkit';

import store, { TDispatch } from '..';
import { IThreadListItem } from './interfaces';
import { threadFetchBuilder } from './fetchers/threads';
import { topicFetchBuilder } from './fetchers/topics';
import { messageFetchBuilder } from './fetchers/messages';
import { attachmentFetchBuilder } from './fetchers/attachment';
import { TTOPIC } from './types/TTopic';
import { patientDataList } from 'Store/session/apiFetchers';
import { TATTACHMENT } from './types/TAttachment';
import { TTHREAD } from './types/TThread';

const LOAD_TOPICS = 'neuro-ui/messaging/LOAD_TOPICS';
const LOAD_THREADLIST = 'neuro-ui/messaging/LOAD_THREADLIST';
const LOAD_PATIENT_NAMES = 'neuro-ui/messaging/LOAD_PATIENT_NAMES';

const loadTopicsAction = createAction(LOAD_TOPICS, (topics: TTOPIC[]) => {
  return {
    payload: { topics },
  };
});

const loadThreadListAction = createAction(LOAD_THREADLIST, (threads: IThreadListItem[]) => {
  return {
    payload: { threads },
  };
});

const loadPatientNamesAction = createAction(LOAD_PATIENT_NAMES, (topicNames: IMessagingStore['patientNames']) => {
  return {
    payload: { topicNames },
  };
});

const loadTopics =
  () =>
  async (dispatch: TDispatch): Promise<boolean> => {
    return topicFetchBuilder<'list'>({ name: 'list' })
      .then(data => {
        if (data) {
          Array.isArray(data) && dispatch(loadTopicsAction(data));
          return true;
        }
        return false;
      })
      .catch(() => {
        return false;
      });
  };

const loadThreadList =
  (onlyPatient?: boolean) =>
  async (dispatch: TDispatch): Promise<IThreadListItem[] | false> => {
    return threadFetchBuilder<'list'>({ name: onlyPatient ? 'listPatient' : 'listOrg' })
      .then(data => {
        if (data) {
          if (Array.isArray(data)) {
            dispatch(loadThreadListAction(data));
            return data;
          }
          return false;
        }
        return false;
      })
      .catch(() => {
        return false;
      });
  };

const newMessageToThread = async (
  patientId: string,
  thread: TTHREAD,
  messageBody?: string | null,
  attachments?: File[],
) => {
  // Create message
  const newMessage = await messageFetchBuilder<'create'>({
    name: 'create',
    queryParams: [patientId, thread.id],
    body: messageBody || null,
  });

  // Send message
  const sentMessage =
    newMessage &&
    (await messageFetchBuilder<'send'>({
      name: 'send',
      queryParams: [patientId, newMessage.id],
    }));

  // Add attachments
  if ((attachments || []).length > 0 && newMessage) {
    const attachPromises: Promise<false | TATTACHMENT>[] = [];
    attachments!.forEach(file =>
      attachPromises.push(
        attachmentFetchBuilder<'createPatient'>({
          name: 'createPatient',
          queryParams: [patientId, thread.topicId, thread.id, newMessage.id],
          body: file,
          contentType: file.type,
        }),
      ),
    );
    await Promise.all(attachPromises);
  }

  return sentMessage ? { ...newMessage } : false;
};

/**
 * Pipeline to create new topic -> thread -> message
 */
const createNewDiscussionWithPatient =
  (topicTitle: string, messageBody: string, tags: string[], attachments?: File[]) =>
  async (dispatch: TDispatch): Promise<boolean> => {
    const userData = store.getState().session.data;
    const patientid = userData!.patientid;

    // Create new topic
    const newTopic = await topicFetchBuilder<'create'>({
      name: 'create',
      body: { title: topicTitle, targetPatients: [patientid], tags: tags },
    });

    // Activate new topic
    const activatedTopic =
      newTopic &&
      (await topicFetchBuilder<'activate'>({
        name: 'activate',
        queryParams: [newTopic.id],
      }));

    // Create new thread
    const newThread =
      activatedTopic && (await threadFetchBuilder<'create'>({ name: 'create', queryParams: [patientid, newTopic.id] }));

    // Create and send new message and attachments
    const sentMessage = newThread && (await newMessageToThread(patientid, newThread, messageBody, attachments));

    // Assign the thread to the message sender
    sentMessage && (await threadFetchBuilder({ name: 'assign', queryParams: [newThread.id, sentMessage.senderId] }));

    return new Promise(() =>
      setTimeout(async () => {
        if (sentMessage) {
          return await loadThreadList(true)(dispatch).then(res => res);
        }
        return false;
      }, 500),
    );
  };

const clearThreads = () => (dispatch: TDispatch) => {
  dispatch(loadThreadListAction([]));
};

const loadPatientNames = (patientIdList: string[], topicId: string, source: string) => (dispatch: TDispatch) => {
  patientDataList(patientIdList, 'patient data fetch for messaging ' + source)
    .then(res => {
      if (Array.isArray(res)) {
        dispatch(loadPatientNamesAction({ [topicId]: res }));
      }
    })
    .catch(() => {});
};

export const actions = {
  loadThreadList,
  loadTopics,
  newMessageToThread,
  createNewDiscussionWithPatient,
  clearThreads,
  loadPatientNames,
  loadPatientNamesAction,
};

const initialState: IMessagingStore = {};

const reducer = createReducer(initialState, builder => {
  builder
    .addCase(loadTopicsAction, (state, action) => {
      state.topics = action.payload.topics;
    })
    .addCase(loadThreadListAction, (state, action) => {
      state.threads = action.payload.threads;
    })
    .addCase(loadPatientNamesAction, (state, action) => {
      state.patientNames = { ...(state.patientNames || {}), ...action.payload.topicNames };
    })

    .addDefaultCase(state => {
      return state;
    });
});

export default reducer;
